/**
 * Input: pointer-lock mouse, keyboard, and gamepad.
 *
 * Mouse deltas accumulate between frames and are drained by `consumeMouse()` exactly
 * once per frame. This keeps look input frame-rate independent without the sub-frame
 * jitter you get from sampling `movementX` directly.
 *
 * Mouse is not required. Look can also come from the arrow keys or a gamepad's right
 * stick, and fire/aim have keyboard and gamepad bindings, so the game is playable on a
 * laptop with no pointing device at all. The arrow keys are therefore reserved for
 * looking and are deliberately NOT aliased to movement — a keyboard-only player needs
 * their right hand on the arrows the way a mouse player needs it on the mouse.
 *
 * Stick and key look are angular *rates* (radians/second, scaled by dt), while mouse
 * look is a displacement per pixel. Mixing the two units is the usual reason keyboard
 * look feels either glacial or uncontrollable.
 */

const BINDINGS = {
  forward: ['KeyW'],
  back: ['KeyS'],
  left: ['KeyA'],
  right: ['KeyD'],
  jump: ['Space'],
  crouch: ['ControlLeft', 'KeyC'],
  sprint: ['ShiftLeft'],
  reload: ['KeyR'],
  use: ['KeyF'],
  melee: ['KeyV'],
  grenade: ['KeyG'],
  /** The second equipment slot. `T` keeps it next to `G` under the same hand. */
  tactical: ['KeyT'],
  swap: ['KeyQ'],
  slot1: ['Digit1'],
  slot2: ['Digit2'],
  slot3: ['Digit3'],
  flashlight: ['KeyL'],
  pause: ['Escape'],

  // Keyboard-only equivalents of the mouse buttons. Placed under the right hand,
  // which is already on the arrow keys.
  fire: ['Enter', 'NumpadEnter', 'Slash'],
  ads: ['ShiftRight', 'Period'],

  lookUp: ['ArrowUp'],
  lookDown: ['ArrowDown'],
  lookLeft: ['ArrowLeft'],
  lookRight: ['ArrowRight'],
};

/** Look tuning for the non-mouse paths. */
const LOOK = {
  /** Peak turn rate for the arrow keys, radians/second. */
  keyRate: 2.4,
  /** Seconds to reach full rate from a standstill. A little ramp makes small
   *  corrections possible; without it every tap is a full-speed swing. */
  keyAccel: 0.14,
  /** Peak turn rate for a fully deflected stick, radians/second. */
  padRate: 3.1,
  /** Stick response curve. >1 gives fine control near centre and speed at the edge. */
  padExponent: 2.2,
  /** Radial deadzone, as a fraction of stick travel. */
  padDeadzone: 0.14,
  /** Pitch is slower than yaw on both — vertical over-correction is the main way
   *  non-mouse look feels bad. */
  pitchScale: 0.72,
};

/** Standard-mapping gamepad buttons we care about. */
const PAD = {
  A: 0, B: 1, X: 2, Y: 3,
  LB: 4, RB: 5, LT: 6, RT: 7,
  BACK: 8, START: 9, LS: 10, RS: 11,
  UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15,
};

export class Input {
  constructor(canvas, events) {
    this.canvas = canvas;
    this.events = events;

    this.down = new Set();
    this.pressedThisFrame = new Set();
    this.releasedThisFrame = new Set();

    this.mouse = { dx: 0, dy: 0, wheel: 0 };
    this.buttons = [false, false, false];
    this.buttonPressed = [false, false, false];
    this.buttonReleased = [false, false, false];

    this.locked = false;
    this.sensitivity = 0.0022; // radians per pixel at 1.0x
    this.adsSensScale = 0.72;
    this.invertY = false;

    /** Gamepad state, refreshed once per frame by `pollGamepad()`. */
    this.pad = {
      connected: false,
      lx: 0, ly: 0, rx: 0, ry: 0,
      buttons: [],
      pressed: [],
      released: [],
    };
    this._padPrev = [];

    /**
     * On-screen stick, written by `TouchInput`. Treated exactly like the gamepad's
     * left stick by `moveVector`: analogue, and overriding the keys when deflected.
     */
    this.touchStick = { x: 0, z: 0, active: false };

    /** Ramp state for arrow-key look, so a tap is a nudge and a hold is a sweep. */
    this._keyLook = { x: 0, y: 0 };

    /** True once any non-mouse look input is used — lets the UI adapt its hints. */
    this.usedKeyboardLook = false;
    this.usedGamepad = false;

    this._bind();
  }

  _bind() {
    const kd = (e) => {
      if (e.repeat) return;
      // Let the browser keep F5/devtools etc.; swallow game keys so the page doesn't scroll.
      // A modified key is never a game input — it is a browser command. Without
      // this check `preventDefault` on `KeyR`/`KeyW` swallowed Cmd-R and Cmd-W,
      // so reloading the page was broken in-game. Both menu screens already guard
      // this way; this was the last offender.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (this._isGameKey(e.code)) {
        e.preventDefault();
        // Any game key doubles as "press any key to deploy" for players without a
        // pointing device. Escape is excluded by `_isGameKey`, so pausing still works.
        this.engage();
      }
      if (!this.down.has(e.code)) this.pressedThisFrame.add(e.code);
      this.down.add(e.code);
    };
    const ku = (e) => {
      this.down.delete(e.code);
      this.releasedThisFrame.add(e.code);
    };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);

    window.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      this.mouse.dx += e.movementX || 0;
      this.mouse.dy += e.movementY || 0;
    });

    window.addEventListener('mousedown', (e) => {
      if (!this.locked) return;
      e.preventDefault();
      if (e.button < 3 && !this.buttons[e.button]) this.buttonPressed[e.button] = true;
      if (e.button < 3) this.buttons[e.button] = true;
    });
    window.addEventListener('mouseup', (e) => {
      if (e.button < 3) {
        this.buttons[e.button] = false;
        this.buttonReleased[e.button] = true;
      }
    });
    window.addEventListener('contextmenu', (e) => this.locked && e.preventDefault());
    window.addEventListener(
      'wheel',
      (e) => {
        if (this.locked) {
          e.preventDefault();
          this.mouse.wheel += Math.sign(e.deltaY);
        }
      },
      { passive: false }
    );

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
      // Dropping lock must not leave keys stuck down (alt-tab, Esc).
      if (!this.locked) {
        this.down.clear();
        this.buttons = [false, false, false];
      }
      this.events.emit('input:lock', { locked: this.locked });
    });

    window.addEventListener('blur', () => {
      this.down.clear();
      this.buttons = [false, false, false];
    });
  }

  _isGameKey(code) {
    return (
      code.startsWith('Key') ||
      code.startsWith('Digit') ||
      code.startsWith('Arrow') ||
      code === 'Space' ||
      code === 'Tab' ||
      code === 'Enter' ||
      code === 'NumpadEnter' ||
      code === 'Slash' ||
      code === 'Period' ||
      code === 'ShiftRight'
    );
  }

  requestLock() {
    if (this.locked) return;
    // In browsers where this returns a promise it rejects whenever the request is
    // declined — no user gesture, a lock still exiting, or no pointing device at
    // all. Dropping it logged an uncaught rejection on every decline. A refused
    // lock is an expected outcome here, not an error: keyboard and gamepad players
    // never acquire one and play through `engage()` instead.
    const p = this.canvas.requestPointerLock?.();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  /**
   * Hand control to the game without pointer lock.
   *
   * The start screen is dismissed by `input:lock`, which only ever fires from
   * `pointerlockchange` — so on a machine with no pointing device the player could
   * not get past it at all. Keyboard and gamepad users engage through here instead.
   * The event carries `virtual: true` for anything that needs to tell the two apart;
   * everything that only asks "can the player drive the game now" works unchanged.
   */
  engage() {
    if (this._engaged) return;
    this._engaged = true;
    this.events.emit('input:lock', { locked: true, virtual: true });
  }

  /**
   * Whether input can currently reach the game.
   *
   * Mouse look needs pointer lock; keyboard and gamepad do not. Without this the
   * keyboard-only path would be dead, since every gameplay read is gated on `locked`.
   */
  get engaged() {
    return this.locked || this.usedKeyboardLook || this.pad.connected;
  }

  /** Held state for a named action. */
  action(name) {
    const keys = BINDINGS[name];
    if (!keys) return false;
    for (const k of keys) if (this.down.has(k)) return true;
    return false;
  }

  /** True only on the frame the action went down. */
  actionPressed(name) {
    const keys = BINDINGS[name];
    if (!keys) return false;
    for (const k of keys) if (this.pressedThisFrame.has(k)) return true;
    return false;
  }

  actionReleased(name) {
    const keys = BINDINGS[name];
    if (!keys) return false;
    for (const k of keys) if (this.releasedThisFrame.has(k)) return true;
    return false;
  }

  /* Fire and aim accept mouse buttons, keyboard keys, or gamepad triggers, so no
     one input device is load-bearing. */
  get fire() {
    return this.buttons[0] || this.action('fire') || this._padDown(PAD.RT);
  }
  get firePressed() {
    return this.buttonPressed[0] || this.actionPressed('fire') || this._padPressed(PAD.RT);
  }
  get ads() {
    return this.buttons[2] || this.action('ads') || this._padDown(PAD.LT);
  }
  get adsPressed() {
    return this.buttonPressed[2] || this.actionPressed('ads') || this._padPressed(PAD.LT);
  }

  _padDown(i) {
    return !!this.pad.buttons[i];
  }
  _padPressed(i) {
    return !!this.pad.pressed[i];
  }

  /** Movement vector in local space, normalised. x = strafe, z = forward. */
  moveVector(out) {
    let x = 0;
    let z = 0;
    if (this.action('forward')) z -= 1;
    if (this.action('back')) z += 1;
    if (this.action('left')) x -= 1;
    if (this.action('right')) x += 1;

    // Left stick overrides the keys when it is actually deflected, so a pad user
    // gets analogue movement without the keys fighting it.
    const px = this.pad.lx;
    const pz = this.pad.ly;
    if (Math.hypot(px, pz) > 0.001) {
      x = px;
      z = pz;
    }

    // The on-screen stick has the last word for the same reason, and after the pad
    // because a device with both is a phone with a controller paired — in which case
    // the thumb that is actually on the glass is the one the player meant.
    if (this.touchStick.active) {
      x = this.touchStick.x;
      z = this.touchStick.z;
    }

    const len = Math.hypot(x, z);
    if (len > 1) {
      x /= len;
      z /= len;
    }
    out.x = x;
    out.z = z;
    return out;
  }

  /**
   * Total look delta for the frame, in radians: mouse displacement plus the
   * keyboard and stick rates integrated over `dt`. Call once per frame.
   */
  consumeMouse(sensScale = 1, dt = 0) {
    const s = this.sensitivity * sensScale;
    let yaw = -this.mouse.dx * s;
    let pitch = (this.invertY ? this.mouse.dy : -this.mouse.dy) * s;
    this.mouse.dx = 0;
    this.mouse.dy = 0;

    if (dt > 0) {
      const k = this._keyLookDelta(dt, sensScale);
      const p = this._padLookDelta(dt, sensScale);
      yaw += k.yaw + p.yaw;
      pitch += k.pitch + p.pitch;
    }
    return { yaw, pitch };
  }

  /** Arrow-key look, ramped so a tap is a nudge and a hold is a steady sweep. */
  _keyLookDelta(dt, sensScale) {
    const tx = (this.action('lookRight') ? 1 : 0) - (this.action('lookLeft') ? 1 : 0);
    const ty = (this.action('lookUp') ? 1 : 0) - (this.action('lookDown') ? 1 : 0);
    if (tx || ty) {
      this.usedKeyboardLook = true;
      this.engage();
    }

    // Ramp toward the target, and fall to zero at the same rate on release so the
    // view settles instead of stopping dead.
    const step = dt / Math.max(LOOK.keyAccel, 1e-4);
    this._keyLook.x += Math.sign(tx - this._keyLook.x) * Math.min(step, Math.abs(tx - this._keyLook.x));
    this._keyLook.y += Math.sign(ty - this._keyLook.y) * Math.min(step, Math.abs(ty - this._keyLook.y));

    const rate = LOOK.keyRate * sensScale * dt;
    const inv = this.invertY ? -1 : 1;
    return {
      yaw: -this._keyLook.x * rate,
      pitch: this._keyLook.y * rate * LOOK.pitchScale * inv,
    };
  }

  /** Right-stick look with a radial deadzone and an expo response curve. */
  _padLookDelta(dt, sensScale) {
    const x = this.pad.rx;
    const y = this.pad.ry;
    const mag = Math.hypot(x, y);
    if (mag < 1e-4) return { yaw: 0, pitch: 0 };

    const curved = Math.pow(Math.min(mag, 1), LOOK.padExponent);
    const rate = (LOOK.padRate * sensScale * dt * curved) / mag;
    const inv = this.invertY ? -1 : 1;
    return {
      yaw: -x * rate,
      pitch: -y * rate * LOOK.pitchScale * inv,
    };
  }

  /**
   * Refresh gamepad state. Must run before gameplay reads input, so the engine
   * calls it on the 'input' phase.
   *
   * The Gamepad API hands out a fresh snapshot object each call rather than a live
   * one, so polling is mandatory — there are no events for axes.
   */
  pollGamepad() {
    const pads = navigator.getGamepads?.() ?? [];
    let gp = null;
    for (const p of pads) {
      if (p && p.connected) {
        gp = p;
        break;
      }
    }
    if (!gp) {
      this.pad.connected = false;
      this.pad.lx = this.pad.ly = this.pad.rx = this.pad.ry = 0;
      this.pad.buttons.length = 0;
      this.pad.pressed.length = 0;
      return;
    }

    this.pad.connected = true;
    const dz = (v) => (Math.abs(v) < LOOK.padDeadzone ? 0 : (v - Math.sign(v) * LOOK.padDeadzone) / (1 - LOOK.padDeadzone));
    this.pad.lx = dz(gp.axes[0] ?? 0);
    this.pad.ly = dz(gp.axes[1] ?? 0);
    this.pad.rx = dz(gp.axes[2] ?? 0);
    this.pad.ry = dz(gp.axes[3] ?? 0);

    this.pad.pressed.length = 0;
    this.pad.released.length = 0;
    for (let i = 0; i < gp.buttons.length; i++) {
      // Triggers are analogue on most pads; treat past half travel as held.
      const now = gp.buttons[i].pressed || gp.buttons[i].value > 0.5;
      const was = !!this._padPrev[i];
      this.pad.buttons[i] = now;
      this.pad.pressed[i] = now && !was;
      this.pad.released[i] = !now && was;
      this._padPrev[i] = now;
      if (now) {
        this.usedGamepad = true;
        this.engage();
      }
    }

    // Map the face and shoulder buttons onto the same named actions the keyboard
    // uses, so gameplay code never has to know which device is driving it.
    this._padAction('jump', PAD.A);
    this._padAction('crouch', PAD.B);
    this._padAction('reload', PAD.X);
    this._padAction('swap', PAD.Y);
    this._padAction('sprint', PAD.LS);
    this._padAction('melee', PAD.RS);
    this._padAction('grenade', PAD.RB);
    this._padAction('tactical', PAD.LB);
    // `use` had LB and has never had a consumer — nothing in the game reads it. The
    // tactical slot is a real action and gets the button rather than sharing it,
    // because two actions on one pad button is a bug waiting for the second one to
    // be implemented.
    this._padAction('pause', PAD.START);
  }

  /** Injects a pad button into the keyboard sets so `action()` sees it uniformly. */
  _padAction(name, index) {
    this.setVirtualAction(`Pad${index}`, name, this.pad.buttons[index]);
  }

  /**
   * Hold or release a named action from a device that has no key code of its own.
   *
   * The synthetic `code` is registered as one more binding for `name` and then
   * driven through the same `down` / `pressedThisFrame` / `releasedThisFrame` sets a
   * real key uses. That is the entire reason gameplay code never branches on input
   * device: by the time anything asks `action('reload')`, a keyboard, a gamepad face
   * button and a thumb on glass are indistinguishable.
   *
   * The code must be unique per device *and* per action — two sources sharing one
   * code would let either of them release a control the other is still holding.
   *
   * @param {string} code   synthetic key code, e.g. `Pad2` or `Touch_reload`
   * @param {string} name   action name from BINDINGS
   * @param {boolean} on    held or released
   */
  setVirtualAction(code, name, on) {
    const keys = BINDINGS[name];
    if (!keys) return;
    if (!keys.includes(code)) keys.push(code);
    if (on) {
      if (!this.down.has(code)) this.pressedThisFrame.add(code);
      this.down.add(code);
    } else if (this.down.has(code)) {
      this.down.delete(code);
      this.releasedThisFrame.add(code);
    }
  }

  /** Clear edge-triggered state. Engine calls this at the very end of the frame. */
  endFrame() {
    this.pressedThisFrame.clear();
    this.releasedThisFrame.clear();
    this.buttonPressed[0] = this.buttonPressed[1] = this.buttonPressed[2] = false;
    this.buttonReleased[0] = this.buttonReleased[1] = this.buttonReleased[2] = false;
    this.mouse.wheel = 0;
  }
}
